import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAlert } from '../../../context/alert';
import useDebounce from '../../../hooks/useDebounce';
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';
import { toUTCStartOfDay, toUTCEndOfDay } from '../../../helpers/dates';
import { locale } from '../../../helpers/strings';
import { useCommentsTreeInfiniteQuery, useDeleteCommentMutation } from '../../../hooks/useCommentsQuery';
import RegularPageLayout from '../../../layouts/RegularPageLayout';
import { CommentResponse, CommentOrderingField } from '../../../types/api/comments';
import { AdsFaqCard } from './components/AdsFaqCard';
import { Placeholder, createErrorPlaceholder } from '../../basic';
import useExportData from '../../../hooks/useExportData';
import { exportComments } from '../../../services/comments';
import { useSorting } from '../../../hooks/useSorting';
import { UserOrderingField } from '../../../types/api/users';
import { pageSize } from '../../../services/settings';
import TableActionsBanner from '../../common/TableActionsBanner';
import AdViewSegment from '../Ad/components/AdViewSegment';
import { DateFormModal } from '../../user/Ads/components/FilterModal/DateFormModal';
import { Stack, SkeletonBox, Button, TextInput, IconButton, Text } from '@primer/react'
import { XCircleIcon, XCircleFillIcon, XIcon, SearchIcon, FilterIcon, DownloadIcon, TrashIcon } from '@primer/octicons-react';

const EMPTY_NUMBERS_ARRAY: number[] = [];
const EMPTY_STRINGS_ARRAY: string[] = [];
const EMPTY_SET = new Set<string>();

export default function AdsFaqList() {
    type AdsFaqAction = 'delete';

    const [searchParams, setSearchParams] = useSearchParams();
    const rawId = searchParams.get('id');
    const adsId = useMemo(() => rawId || undefined, [rawId]);

    const reviewsListRef = useRef<HTMLDivElement | null>(null);

    const [searchQuery, setSearchQuery] = useState<string>('');
    const debouncedSearchQuery = useDebounce(searchQuery, 500)

    const { isLoading: isLoadingExport, exportAction } = useExportData();
    const { mutate: deleteCommentsRequest, isPending: isPendingDeleting } = useDeleteCommentMutation();
    const { setSuccess, setInfo, setError, addCustomAlert, updateAlert, removeAlert } = useAlert();

    // Filters Modal
    const [dateFilterOpened, setDateFilterOpened] = useState<boolean>(false);

    // Filter request params
    const [fromDate, setFromDate] = useState<string>('');
    const [toDate, setToDate] = useState<string>('');

    // Filter draft params for modal
    const [fromDateDraft, setFromDateDraft] = useState<string>('');
    const [toDateDraft, setToDateDraft] = useState<string>('');

    // Prepare filter request params
    const createdFromIsoDate = fromDate ? toUTCStartOfDay(fromDate) : undefined;
    const createdToIsoDate = toDate ? toUTCEndOfDay(toDate) : undefined;

    // Select
    const [selectedRows, setSelectedRows] = useState<Set<string>>(EMPTY_SET);
    const [actionsAlertId, setActionsAlertId] = useState<string | undefined>(undefined);

    useEffect(() => {
        setSelectedRows(EMPTY_SET);
    }, [debouncedSearchQuery]);

    const {
        orderingParam,
        handleSort,
        getSortIcon,
        getSortText
    } = useSorting<CommentOrderingField>('created_at');
    const orderingParams: CommentOrderingField = orderingParam as CommentOrderingField;

    const {
        isLoading,
        data,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        refetch
    } = useCommentsTreeInfiniteQuery(
        adsId ?? "", //entityId
        "ad", //entityType
        false, //is_admin
        pageSize, //page
        createdFromIsoDate, //created_from
        createdToIsoDate, //created_to
        debouncedSearchQuery, //search
        undefined, //user_id,
        orderingParams ?? undefined //ordering
    );

    const handleScroll = useInfiniteScroll(
        reviewsListRef,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        data
    );

    const clearSearchInput = () => {
        setSearchQuery("")
    }

    const emptySearchListDescription = () => {
        return `По вашему запросу не найдено
    подходящих комментариев`
    }

    const emptyListDescription = () => {
        return `Для этого объявления ещё нет
        вопросов и комментариев`
    }

    // Filter modal
    const dateFormError =
        fromDate !== '' &&
        toDate !== '' &&
        new Date(fromDate) > new Date(toDate);

    const handleDismiss = () => {
        setFromDate('');
        setToDate('');

        setFromDateDraft('');
        setToDateDraft('');

        setDateFilterOpened(false);
    }
    const handleApply = () => {
        setFromDate(fromDateDraft);
        setToDate(toDateDraft);

        setDateFilterOpened(false);
    }

    useEffect(() => {
        if (!dateFilterOpened) {
            // Сброс изменений, если закрыли кликом по фону
            setFromDateDraft(fromDate);
            setToDateDraft(toDate);
        }
    }, [dateFilterOpened]);

    // EXPORT COMMENTS
    const downloadComments = async () => {
        if (adsId) {
            try {
                const result = await exportAction(() => exportComments(adsId, "ad", false), "comments", "xlsx");
                if (result.success) {
                    setSuccess('Загрузка списка вопросов успешно началась');
                }
            } catch (err) { }
        }
    }

    useEffect(() => {
        let selectedItemsCount = selectedRows.size
        if (selectedItemsCount > 0) {
            handleAddTableAlert(selectedItemsCount)
        } else {
            if (actionsAlertId) {
                removeAlert(actionsAlertId)
                setActionsAlertId(undefined)
            }
        }
    }, [selectedRows])

    function createConfirmDeleteAlertProps(count: number) {
        return {
            count: count,
            title: count > 1 ? 'Точно хотите удалить?' : 'Точно хотите удалить комментарий?',
            buttons: [
                <Button key="clear-button" variant="default" onClick={showTableActionsAlert}>
                    Нет
                </Button>,
                <Button key="clear-button" variant="danger" onClick={deleteSelectedComments}>
                    Да, удалить
                </Button>
            ],
            onDismiss: () => { },
            didDismiss: () => {
                setActionsAlertId(undefined)
                // Uncheck table
                setSelectedRows(EMPTY_SET);
            }
        }
    }

    function createConfirmDeleteOneCommentAlertProps(id: string) {
        return {
            count: 1,
            title: 'Точно хотите удалить комментарий?',
            buttons: [
                <Button key="clear-button" variant="default" onClick={dismissDeleteOneCommentAlert}>
                    Нет
                </Button>,
                <Button key="clear-button" variant="danger" onClick={() => deleteComments([id])}>
                    Да, удалить
                </Button>
            ],
            onDismiss: () => { },
            didDismiss: () => {
                setActionsAlertId(undefined)
            }
        }
    }

    const useUserAction = (action: AdsFaqAction, actionsAlertId?: string) => {
        return useCallback(() => {
            let selectedItems = selectedRowsRef.current;

            if (selectedItems && selectedItems.size > 0) {
                const ids = Array.from(selectedItems);

                switch (action) {
                    case 'delete':
                        deleteComments(ids);
                        break;
                    default:
                        console.error('Unknown action:', action);
                }
            }
        }, [action]);
    };

    const deleteComments = (ids: string[]) => {
        deleteCommentsRequest(ids, {
            onSuccess: (data) => {
                handleUsersActionResult(ids.length, 'Комментарий удален', ids.length + ' комментария удалено')
                if (data.failed > 0) {
                    setError(`Ошибок: ${data.failed}`);
                }
            }
        });
    };

    const showConfirmDeleteAlert = useCallback(() => {
        let selectedItemsCount = selectedRowsRef.current.size
        const props = createConfirmDeleteAlertProps(selectedItemsCount)
        showUpdatedAlert(props)
    }, [actionsAlertId]);

    const deleteSelectedComments = useUserAction('delete', actionsAlertId);

    const handleUsersActionResult = (
        count: number,
        singularMessage: string,
        pluralMessage: string
    ) => {
        // Update UI
        dismissTableActionAlert()
        setSelectedRows(EMPTY_SET);
        refetch();
        // Alert
        if (count === 1) {
            setInfo(singularMessage);
        } else {
            setInfo(pluralMessage);
        }
    };


    // ALERT HELPERS
    const dismissTableActionAlert = () => {
        let alertId = actionsAlertIdRef.current
        if (alertId) {
            removeAlert(alertId)
            setActionsAlertId(undefined)
            setSelectedRows(EMPTY_SET);
        }
    }

    const actionsAlertIdRef = useRef(actionsAlertId);
    useEffect(() => {
        actionsAlertIdRef.current = actionsAlertId;
    }, [actionsAlertId])

    const selectedRowsRef = useRef(selectedRows);
    useEffect(() => {
        selectedRowsRef.current = selectedRows;
    }, [selectedRows])

    const showTableActionsAlert = useCallback(() => {
        let selectedItems = selectedRowsRef.current
        let count = Array.from(selectedItems).length
        handleAddTableAlert(count)
    }, [actionsAlertId]);


    const dismissDeleteOneCommentAlert = useCallback(() => {
        setActionsAlertId(undefined)
        dismissTableActionAlert()
    }, [actionsAlertId]);

    const handleAddTableAlert = useCallback((count: number) => {
        let props = createDeleteAlertProps(count)

        let alertId = actionsAlertIdRef.current
        if (alertId) {
            updateAlert(alertId, {
                customProps: props
            });
        } else {
            const alertId = addCustomAlert(
                TableActionsBanner,
                props,
                {
                    autoClose: false,
                }
            );

            setActionsAlertId(alertId)
        }
    }, [actionsAlertId, data]);

    const showUpdatedAlert = useCallback((customProps: Record<string, any>) => {
        let selectedItemsCount = selectedRowsRef.current.size
        const alertId = actionsAlertIdRef.current
        if (alertId) {
            updateAlert(alertId, {
                customProps: customProps
            });
        }
    }, [actionsAlertId]);

    const displayDeleteOneCommentAlert = useCallback((id: string) => {
        setActionsAlertId(undefined)
        dismissTableActionAlert()

        let props = createConfirmDeleteOneCommentAlertProps(id)
        const alertId = addCustomAlert(
            TableActionsBanner,
            props,
            {
                autoClose: false,
            }
        );

        setActionsAlertId(alertId)
        actionsAlertIdRef.current = alertId
    }, [actionsAlertId, data,]);

    // TABLE ACTIONS ALERT
    function createDeleteAlertProps(count: number) {
        const localStr = locale.selected(count)
        const title = localStr && localStr[0].toUpperCase() + localStr.slice(1);
        return {
            count: count,
            title: title,
            buttons: [
                <Button key="clear-button" variant="default" leadingVisual={TrashIcon} onClick={showConfirmDeleteAlert}>
                    Удалить
                </Button>
            ],
            onDismiss: () => { },
            didDismiss: () => {
                setActionsAlertId(undefined)
                // Uncheck table
                setSelectedRows(EMPTY_SET);
            }
        }
    }

    // SELECT ROWS
    const headerCheckboxState = useMemo(() => {
        const allItems = data?.pages.flatMap(page => page.list) || [];
        if (data && allItems.length > 0) {
            const totalRows = allItems.length;
            const selectedFromCurrentData = allItems.filter(row => selectedRows.has(row.id)).length;

            if (selectedFromCurrentData === 0) {
                return { checked: false, indeterminate: false };
            } else if (selectedFromCurrentData === totalRows && totalRows > 0) {
                return { checked: true, indeterminate: false };
            } else {
                return { checked: false, indeterminate: true };
            }
        }
        return { checked: false, indeterminate: false };
    }, [data, selectedRows]);

    const handleRowSelect = useCallback((rowId: string, isSelected: boolean) => {
        rowSelect(rowId, isSelected)
    }, [handleAddTableAlert]);


    const rowSelect = useCallback((rowId: string, isSelected: boolean) => {
        setSelectedRows(prev => {
            const newSelection = new Set(prev);
            if (isSelected) {
                newSelection.add(rowId);
            } else {
                newSelection.delete(rowId);
            }
            return newSelection;
        });

    }, [handleAddTableAlert]);

    return (
        <RegularPageLayout
            header={
                <Stack justify="space-between" direction="horizontal" gap="condensed" className="mr-4">

                    <AdViewSegment id={searchParams.get('id') || ''} />
                    <Stack direction="horizontal">
                        <TextInput
                            leadingVisual={SearchIcon}
                            trailingAction={
                                searchQuery.length > 0 ? (
                                    <TextInput.Action
                                        onClick={() => {
                                            setSearchQuery('');
                                        }}
                                        icon={XCircleFillIcon}
                                        aria-label="Очистить"
                                    />
                                ) : undefined
                            }
                            value={searchQuery}
                            placeholder='Поиск'
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                            }}
                        />

                        <IconButton
                            icon={FilterIcon}
                            onClick={() => !dateFilterOpened && setDateFilterOpened(true)}
                            aria-label="Фильтр" />

                        <DateFormModal
                            dateFilterOpened={dateFilterOpened}
                            setDateFilterOpened={setDateFilterOpened}
                            fromDate={fromDateDraft}
                            setFromDate={setFromDateDraft}
                            toDate={toDateDraft}
                            setToDate={setToDateDraft}
                            dateFormError={dateFormError}
                            onDismiss={handleDismiss}
                            onApply={handleApply}
                            isShowBackButton={false}
                        />

                        <IconButton
                            icon={DownloadIcon}
                            aria-label="Выгрузить списка вопросов"
                            loading={isLoadingExport}
                            onClick={downloadComments} />
                    </Stack>
                </Stack>
            }
            content={(
                <Stack className='mt-14'>

                    <Stack justify="space-between" direction="horizontal" className='-mr-2'>
                        <div></div>
                        <button onClick={() => handleSort('created_at')}>
                            <Stack align="center" direction="horizontal" gap='none'>
                                <div className='text-aloix-grey'>
                                    {getSortIcon('created_at')}
                                </div>
                                <Text
                                    as="span"
                                    weight='medium'
                                    className='text-[12px] text-aloix-grey mr-2 ml-[4.5px]'>
                                    {getSortText('created_at', 'Дата размещения')}
                                </Text>
                            </Stack>
                        </button>
                    </Stack>

                    <div
                        ref={reviewsListRef}
                        onScroll={handleScroll}
                        className="gap-4 max-h-screen overflow-y-auto"
                    >
                        {
                            data?.pages.map((page, i) => (
                                <React.Fragment key={i}>
                                    {page.list.map((e: CommentResponse, index) => (
                                        <AdsFaqCard
                                            key={e.id}
                                            comment={e}
                                            highlight={searchQuery}
                                            isSelected={selectedRows.has(e.id)}
                                            isDisabled={isPendingDeleting}
                                            didSelected={(isSelected) => handleRowSelect(e.id, isSelected)}
                                            didDelete={() => displayDeleteOneCommentAlert(e.id)}
                                        />
                                    ))}
                                </React.Fragment>
                            ))
                        }
                    </div>
                    {!isLoading && !isFetchingNextPage && (data?.pages?.flatMap(page => page.list).length ?? 0) === 0 && (
                        <Placeholder
                            icon={<XCircleIcon size={24} className='opacity-40' />}
                            header='Вопросов и ответов не найдено'
                            description={searchQuery.length > 0 ? emptySearchListDescription() : emptyListDescription()}
                            button={searchQuery.length > 0 && <Button
                                variant="default"
                                leadingVisual={XIcon}
                                onClick={clearSearchInput}>
                                Очистить</Button>}
                        />
                    )}
                    {/*Индикатор загрузки*/}
                    {(isFetchingNextPage || isLoading) &&
                        <SkeletonBox height="4rem" />
                    }
                </Stack>
            )}
        />
    );
}